import {TextField} from '@wandb/weave/components/Form/TextField';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {createPortal} from 'react-dom';

import {Messages} from '../../../ChatView/types';
import {WEAVE_REF_SCHEME} from '../../../wfReactInterface/constants';
import {objectVersionKeyToRefUri} from '../../../wfReactInterface/utilities';
import {PromptOption, PromptVersion} from './usePromptOptions';

interface PromptDropdownProps {
  entity: string;
  project: string;
  value?: string;
  onChange: (promptName: string, messages: Messages, promptRef: string) => void;
  onClear?: () => void;
  className?: string;
  promptOptions: PromptOption[];
  loading?: boolean;
  isModified?: boolean;
}

export interface PromptDropdownRef {
  close: () => void;
}

// Component spacing constants
const DROPDOWN_GAP_PX = 4;
// Component width constants
const MIN_WIDTH = 226;
const MAX_WIDTH = 448;
const ICON_PADDING = 34;
const ACTIONS_PADDING = 56;

// Focus navigation constants
const NO_FOCUS = null; // null indicates no item is focused

// Debounce delay for search input
const DEBOUNCE_DELAY_MS = 300;

/**
 * Custom hook to debounce a value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

const parseValue = (
  value: string | undefined
): [string | null, number | null] => {
  if (!value) {
    return [null, null];
  }
  // Value format is "promptName:vX" where X is the version index
  const parts = value.split(':v');
  if (parts.length === 2) {
    const versionIndex = parseInt(parts[1], 10);
    return [parts[0], isNaN(versionIndex) ? null : versionIndex];
  }
  return [value, null];
};

// Reducer state and actions
type SelectorState = {
  // Dropdown state
  isOpen: boolean;
  dropdownPosition: {top: number; left: number} | null;

  // Navigation & focus (null = no focus, 0+ = items)
  activePane: 'prompts' | 'versions';
  focusedIndex: number | null;
  versionFocusedIndex: number | null;

  // Search & text field
  textFieldValue: string;
  searchQuery: string;

  // Display state
  displayedPromptLabel: string | null; // which prompt's versions to show
  measuredWidth: number;
};

type SelectorAction =
  | {type: 'OPEN_DROPDOWN'; position: {top: number; left: number}}
  | {type: 'CLOSE_DROPDOWN'}
  | {type: 'SET_FOCUS'; index: number | null}
  | {type: 'SET_VERSION_FOCUS'; index: number | null}
  | {type: 'SET_ACTIVE_PANE'; pane: 'prompts' | 'versions'}
  | {type: 'SET_TEXT_FIELD_VALUE'; value: string}
  | {type: 'SET_SEARCH_QUERY'; query: string}
  | {type: 'SET_DISPLAYED_PROMPT'; label: string | null}
  | {type: 'SET_MEASURED_WIDTH'; width: number}
  | {type: 'RESET_FOR_OPEN'};

function selectorReducer(
  state: SelectorState,
  action: SelectorAction
): SelectorState {
  switch (action.type) {
    case 'OPEN_DROPDOWN':
      return {
        ...state,
        isOpen: true,
        dropdownPosition: action.position,
      };
    case 'CLOSE_DROPDOWN':
      return {
        ...state,
        isOpen: false,
      };
    case 'SET_FOCUS':
      return {
        ...state,
        focusedIndex: action.index,
      };
    case 'SET_VERSION_FOCUS':
      return {
        ...state,
        versionFocusedIndex: action.index,
      };
    case 'SET_ACTIVE_PANE':
      return {
        ...state,
        activePane: action.pane,
      };
    case 'SET_TEXT_FIELD_VALUE':
      return {
        ...state,
        textFieldValue: action.value,
      };
    case 'SET_SEARCH_QUERY':
      return {
        ...state,
        searchQuery: action.query,
      };
    case 'SET_DISPLAYED_PROMPT':
      return {
        ...state,
        displayedPromptLabel: action.label,
      };
    case 'SET_MEASURED_WIDTH':
      return {
        ...state,
        measuredWidth: action.width,
      };
    case 'RESET_FOR_OPEN':
      return {
        ...state,
        searchQuery: '',
        focusedIndex: NO_FOCUS,
        versionFocusedIndex: NO_FOCUS,
        activePane: 'prompts',
      };
    default:
      return state;
  }
}

/**
 * A dropdown component for selecting prompts and their versions.
 *
 * Displays a two-pane interface:
 * - Left pane (70%): scrollable list of prompt names
 * - Right pane (30%): versions for the selected prompt
 *
 * Features:
 * - TextField trigger with search integration
 * - Dynamic width adjustment based on selected value
 * - Full keyboard navigation (arrow keys, enter, escape)
 * - Loading states and visual feedback
 */
export const PromptDropdown = forwardRef<
  PromptDropdownRef,
  PromptDropdownProps
>(
  (
    {
      entity,
      project,
      value,
      onChange,
      onClear,
      className,
      promptOptions,
      loading = false,
      isModified = false,
    },
    ref
  ) => {
    // Use useReducer for all UI state
    const [state, dispatch] = useReducer(selectorReducer, {
      isOpen: false,
      dropdownPosition: null,
      activePane: 'prompts',
      focusedIndex: NO_FOCUS,
      versionFocusedIndex: NO_FOCUS,
      textFieldValue: '',
      searchQuery: '',
      displayedPromptLabel: null,
      measuredWidth: MIN_WIDTH,
    });

    // Expose close method to parent via ref
    useImperativeHandle(ref, () => ({
      close: () => {
        dispatch({type: 'CLOSE_DROPDOWN'});
      },
    }));

    // Debounce search query for filtering
    const debouncedSearchQuery = useDebounce(
      state.searchQuery,
      DEBOUNCE_DELAY_MS
    );

    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const focusedItemRef = useRef<HTMLDivElement>(null);
    const measuringRef = useRef<HTMLSpanElement>(null);

    // Filter prompts based on debounced search query
    const filteredPrompts = useMemo(() => {
      if (!debouncedSearchQuery.trim()) {
        return promptOptions;
      }
      const lowerQuery = debouncedSearchQuery.toLowerCase();
      return promptOptions.filter(prompt =>
        prompt.label.toLowerCase().includes(lowerQuery)
      );
    }, [promptOptions, debouncedSearchQuery]);

    const [selectedPromptName, selectedVersionIndex] = useMemo(
      () => parseValue(value),
      [value]
    );

    // Get versions for the displayed prompt
    const displayedVersions = useMemo(() => {
      if (!state.displayedPromptLabel) {
        return [];
      }
      const prompt = promptOptions.find(
        p => p.label === state.displayedPromptLabel
      );
      return prompt?.versions || [];
    }, [state.displayedPromptLabel, promptOptions]);

    const handlePromptClick = useCallback(
      (prompt: PromptOption) => {
        dispatch({type: 'SET_DISPLAYED_PROMPT', label: prompt.label});

        // Select the latest version by default
        if (prompt.latestVersion) {
          const objectId = prompt.latestVersion.value.split(':')[0];
          const promptRef = objectVersionKeyToRefUri({
            scheme: WEAVE_REF_SCHEME,
            entity,
            project,
            weaveKind: 'object',
            objectId,
            versionHash: prompt.latestVersion.digest,
            path: '',
            refExtra: '',
          });
          onChange(
            prompt.latestVersion.value,
            prompt.latestVersion.messages,
            promptRef
          );
          dispatch({
            type: 'SET_TEXT_FIELD_VALUE',
            value: prompt.latestVersion.label,
          });
          dispatch({type: 'SET_SEARCH_QUERY', query: ''});
          dispatch({type: 'CLOSE_DROPDOWN'});
        }
      },
      [entity, project, onChange]
    );

    const handleVersionClick = useCallback(
      (version: PromptVersion) => {
        const objectId = version.value.split(':')[0];
        const promptRef = objectVersionKeyToRefUri({
          scheme: WEAVE_REF_SCHEME,
          entity,
          project,
          weaveKind: 'object',
          objectId,
          versionHash: version.digest,
          path: '',
          refExtra: '',
        });
        onChange(version.value, version.messages, promptRef);
        dispatch({
          type: 'SET_TEXT_FIELD_VALUE',
          value: version.label,
        });
        dispatch({type: 'SET_SEARCH_QUERY', query: ''});
        dispatch({type: 'CLOSE_DROPDOWN'});
      },
      [entity, project, onChange]
    );

    const handleClear = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch({type: 'SET_DISPLAYED_PROMPT', label: null});
        if (onClear) {
          onClear();
        } else {
          onChange('', [], '');
        }
        dispatch({type: 'CLOSE_DROPDOWN'});
      },
      [onChange, onClear]
    );

    // Unified keyboard navigation handler for both panes
    const handlePaneKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const isPromptsPane = state.activePane === 'prompts';
        const isVersionsPane = state.activePane === 'versions';

        // Guard: versions pane requires displayedPromptLabel
        if (isVersionsPane && !state.displayedPromptLabel) {
          return;
        }

        // Get pane-specific configuration
        const focusedIndex = isPromptsPane
          ? state.focusedIndex
          : state.versionFocusedIndex;
        const maxIndex = isPromptsPane
          ? filteredPrompts.length - 1
          : displayedVersions.length - 1;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const currentIndex = focusedIndex ?? -1;
          const nextIndex = Math.min(currentIndex + 1, maxIndex);
          dispatch({
            type: isPromptsPane ? 'SET_FOCUS' : 'SET_VERSION_FOCUS',
            index: nextIndex,
          });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const currentIndex = focusedIndex ?? 0;
          const nextIndex = Math.max(currentIndex - 1, 0);
          dispatch({
            type: isPromptsPane ? 'SET_FOCUS' : 'SET_VERSION_FOCUS',
            index: nextIndex,
          });
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (focusedIndex !== null) {
            if (isPromptsPane) {
              const prompt = filteredPrompts[focusedIndex];
              if (prompt) {
                handlePromptClick(prompt);
              }
            } else {
              const version = displayedVersions[focusedIndex];
              if (version) {
                handleVersionClick(version);
              }
            }
          }
        } else if (
          e.key === 'ArrowRight' &&
          isPromptsPane &&
          state.displayedPromptLabel
        ) {
          // Prompts pane: switch to versions pane
          e.preventDefault();
          dispatch({type: 'SET_ACTIVE_PANE', pane: 'versions'});
          dispatch({type: 'SET_VERSION_FOCUS', index: 0});
        } else if (e.key === 'ArrowLeft' && isVersionsPane) {
          // Versions pane: switch to prompts pane
          e.preventDefault();
          dispatch({type: 'SET_ACTIVE_PANE', pane: 'prompts'});
        } else if (e.key === 'Escape') {
          e.preventDefault();
          dispatch({type: 'CLOSE_DROPDOWN'});
        }
      },
      [
        state.activePane,
        state.focusedIndex,
        state.versionFocusedIndex,
        state.displayedPromptLabel,
        filteredPrompts,
        displayedVersions,
        handlePromptClick,
        handleVersionClick,
      ]
    );

    // Click outside handler
    useEffect(() => {
      if (!state.isOpen) {
        return;
      }

      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          dispatch({type: 'CLOSE_DROPDOWN'});
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [state.isOpen]);

    // Scroll focused item into view
    useEffect(() => {
      if (focusedItemRef.current) {
        focusedItemRef.current.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }, [state.focusedIndex, state.versionFocusedIndex, state.activePane]);

    // Compute display value for TextField
    const displayValue = useMemo(() => {
      // When user is typing (panel is open), show their input
      if (state.isOpen) {
        return state.textFieldValue;
      }
      // When not typing, show the selected value
      if (!selectedPromptName) {
        return '';
      }
      // Find the selected prompt to get its label
      const selectedPrompt = promptOptions.find(p => {
        return p.versions.some(
          v => parseValue(v.value)[0] === selectedPromptName
        );
      });
      let baseValue = selectedPromptName;
      if (selectedPrompt && selectedVersionIndex !== null) {
        const selectedVersion = selectedPrompt.versions.find(
          v => v.versionIndex === selectedVersionIndex
        );
        baseValue = selectedVersion?.label || selectedPromptName;
      }

      return baseValue;
    }, [
      state.isOpen,
      state.textFieldValue,
      selectedPromptName,
      selectedVersionIndex,
      promptOptions,
    ]);

    // Measure actual text width and calculate container width
    useLayoutEffect(() => {
      if (measuringRef.current) {
        const textWidth = measuringRef.current.getBoundingClientRect().width;
        // Add padding: icon (ICON_PADDING) + ACTIONS_PADDING (for close + chevron)
        const iconPadding = ICON_PADDING;
        const totalWidth = textWidth + iconPadding + ACTIONS_PADDING;
        // Clamp between MIN_WIDTH and MAX_WIDTH
        const clampedWidth = Math.max(
          MIN_WIDTH,
          Math.min(MAX_WIDTH, totalWidth)
        );
        dispatch({type: 'SET_MEASURED_WIDTH', width: clampedWidth});
      }
    }, [displayValue]);

    const handleTextFieldFocus = useCallback(() => {
      if (!loading) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          dispatch({
            type: 'OPEN_DROPDOWN',
            position: {
              top: rect.bottom + DROPDOWN_GAP_PX,
              left: rect.left,
            },
          });
        }
        // Reset state for new dropdown interaction
        dispatch({type: 'RESET_FOR_OPEN'});
        // Clear the field so user can start typing immediately
        dispatch({type: 'SET_TEXT_FIELD_VALUE', value: ''});

        // Initialize focus and displayed prompt based on current selection
        if (selectedPromptName) {
          const selectedPrompt = promptOptions.find(p => {
            return p.versions.some(
              v => parseValue(v.value)[0] === selectedPromptName
            );
          });

          if (selectedPrompt) {
            const promptIndex = filteredPrompts.findIndex(
              p => p.label === selectedPrompt.label
            );
            if (promptIndex >= 0) {
              dispatch({type: 'SET_FOCUS', index: promptIndex});
            }

            if (selectedVersionIndex !== null) {
              dispatch({
                type: 'SET_DISPLAYED_PROMPT',
                label: selectedPrompt.label,
              });
              const versionIndex = selectedPrompt.versions.findIndex(
                v => v.versionIndex === selectedVersionIndex
              );
              if (versionIndex >= 0) {
                dispatch({type: 'SET_VERSION_FOCUS', index: versionIndex});
                dispatch({type: 'SET_ACTIVE_PANE', pane: 'versions'});
              }
            }
          }
        }
      }
    }, [
      loading,
      selectedPromptName,
      selectedVersionIndex,
      promptOptions,
      filteredPrompts,
    ]);

    const handleTextFieldChange = useCallback((value: string) => {
      dispatch({type: 'SET_TEXT_FIELD_VALUE', value});
      dispatch({type: 'SET_SEARCH_QUERY', query: value});
      // Reset focus when search changes so user starts from top of filtered list
      dispatch({type: 'SET_FOCUS', index: NO_FOCUS});
    }, []);

    // Intercept keyboard events from TextField and delegate to navigation handler
    const handleTextFieldKeyDown = useCallback(
      (key: string, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!state.isOpen) {
          return;
        }

        const navigationKeys = [
          'ArrowUp',
          'ArrowDown',
          'ArrowLeft',
          'ArrowRight',
          'Enter',
          'Escape',
        ];

        if (navigationKeys.includes(e.key)) {
          e.preventDefault();
          handlePaneKeyDown(e);
        }
      },
      [state.isOpen, handlePaneKeyDown]
    );

    return (
      <div
        ref={containerRef}
        className={`tw-style text-[16px] relative min-w-[226px] max-w-[448px] ${className || ''}`}
        style={{width: `${state.measuredWidth}px`}}>
        <style>{`
        .tw-style input {
          font-size: 16px !important;
        }
      `}</style>
        {/* Hidden measuring element */}
        <span
          ref={measuringRef}
          style={{
            visibility: 'hidden',
            position: 'absolute',
            whiteSpace: 'pre',
            fontSize: '16px',
            fontFamily: 'inherit',
          }}>
          {displayValue || 'Select a prompt...'}
        </span>
        <TextField
          value={displayValue}
          onChange={handleTextFieldChange}
          onFocus={handleTextFieldFocus}
          onKeyDown={handleTextFieldKeyDown}
          disabled={loading}
          placeholder={loading ? 'Loading prompts...' : 'Select a prompt...'}
          icon="forum-chat-bubble"
          extraActions={
            <>
              {value && !loading && (
                <button
                  onClick={handleClear}
                  className="flex items-center justify-center p-0 mr-8 border-none bg-transparent cursor-pointer text-moon-500 hover:text-moon-800">
                  <Icon name="close" width={16} height={16} />
                </button>
              )}
              <Icon
                name={state.isOpen ? 'chevron-up' : 'chevron-down'}
                width={16}
                height={16}
                className="mr-8"
              />
            </>
          }
        />

        {state.isOpen &&
          state.dropdownPosition &&
          createPortal(
            <Tailwind>
              <div
                ref={dropdownRef}
                tabIndex={0}
                className="fixed bg-white border border-moon-250 rounded flex w-[540px] max-h-[320px]"
                style={{
                  top: `${state.dropdownPosition.top}px`,
                  left: `${state.dropdownPosition.left}px`,
                  zIndex: 2000,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  outline: 'none',
                }}
                onKeyDown={handlePaneKeyDown}>
                {/* Left pane: Prompts list */}
                <div className="flex-[1_1_70%] flex flex-col border-r border-moon-250">
                  <div
                    className="scrollbar-hover overflow-y-auto py-6 flex-1"
                    style={{direction: 'rtl'}}>
                    <div style={{direction: 'ltr'}}>
                      {loading ? (
                        <div className="p-12 text-moon-500 text-center flex items-center justify-center gap-8">
                          <Icon name="loading" width={16} height={16} />
                          Loading prompts...
                        </div>
                      ) : filteredPrompts.length === 0 ? (
                        <div className="p-12 text-moon-500 text-center">
                          {state.searchQuery
                            ? 'No matching prompts found'
                            : 'No prompts available'}
                        </div>
                      ) : (
                        filteredPrompts.map((prompt, index) => {
                          const isFocused =
                            state.activePane === 'prompts' &&
                            index === state.focusedIndex;
                          const isActive =
                            selectedPromptName === parseValue(prompt.value)[0];
                          return (
                            <Tooltip
                              key={prompt.value}
                              trigger={
                                <div
                                  ref={isFocused ? focusedItemRef : undefined}
                                  onClick={() => handlePromptClick(prompt)}
                                  className={`
                                  py-6 px-10 mx-6 rounded cursor-pointer line-clamp-1
                                  ${isActive ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal'}
                                  ${!isActive ? 'hover:bg-moon-100' : ''}
                                  ${isFocused ? 'bg-moon-100' : ''}
                                `}>
                                  {prompt.label}
                                </div>
                              }
                              content={prompt.label}
                            />
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>

                {/* Right pane: Versions list */}
                {state.displayedPromptLabel && (
                  <div className="flex flex-col flex-[1_1_30%] max-w-[30%]">
                    <div className="scrollbar-hover overflow-y-auto py-6 flex-1">
                      {displayedVersions.length === 0 ? (
                        <div className="p-12 text-moon-500 text-center">
                          No versions found
                        </div>
                      ) : (
                        displayedVersions.map((version, index) => {
                          const isSelected =
                            selectedPromptName ===
                              parseValue(version.value)[0] &&
                            selectedVersionIndex === version.versionIndex;
                          const isFocused =
                            state.activePane === 'versions' &&
                            state.versionFocusedIndex === index;
                          return (
                            <div
                              key={version.value}
                              ref={isFocused ? focusedItemRef : undefined}
                              onClick={() => handleVersionClick(version)}
                              className={`
                              py-6 px-10 mx-6 rounded cursor-pointer flex flex-col gap-2
                              ${isSelected ? 'text-teal-600 bg-teal-300/35 font-semibold' : 'text-moon-800 font-normal hover:bg-moon-100'}
                              ${isFocused ? 'bg-moon-100' : ''}
                            `}>
                              <div className="flex items-center gap-4">
                                <span>v{version.versionIndex}</span>
                                {version.isLatest && <span>(latest)</span>}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Tailwind>,
            document.body
          )}
      </div>
    );
  }
);
